<template>
  <AppLayout title="Benutzer einladen">
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div class="flex justify-between items-center">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">{{ $t('Neuen Benutzer einladen') }}</h1>
              <p class="text-sm text-gray-600 mt-1">{{ $t('Laden Sie einen Mitarbeiter ein, Zugriff auf dieses Konto zu erhalten') }}</p>
            </div>
            <Link 
              :href="route('customerAccount')" 
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {{ $t('Zurück') }}
            </Link>
          </div>
        </div>
      </div>

      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Success Message -->
        <div v-if="successMessage" class="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-green-700">{{ successMessage }}</p>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="Object.keys(errors).length > 0" class="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">{{ $t('Es gab Fehler bei Ihrer Eingabe') }}</h3>
              <div class="mt-2 text-sm text-red-700">
                <ul class="list-disc pl-5 space-y-1">
                  <li v-for="(error, field) in errors" :key="field">{{ error }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="sendInvitation" class="bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('Benutzerdetails') }}</h2>
          </div>
          
          <div class="p-6 space-y-6">
            <!-- Name -->
            <div>
              <label for="name" class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('Name') }}
                <span class="text-red-500">*</span>
              </label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                :class="{ 'border-red-300': errors.name }"
                placeholder="Max Mustermann"
              />
              <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('E-Mail-Adresse') }}
                <span class="text-red-500">*</span>
              </label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                :class="{ 'border-red-300': errors.email }"
                placeholder="max@example.com"
              />
              <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
              <p class="mt-1 text-sm text-gray-500">{{ $t('Eine Einladungs-E-Mail wird an diese Adresse gesendet') }}</p>
            </div>

            <!-- Permissions Info -->
            <div class="bg-blue-50 rounded-lg p-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-blue-800">{{ $t('Benutzerberechtigungen') }}</h3>
                  <div class="mt-2 text-sm text-blue-700">
                    <ul class="list-disc pl-5 space-y-1">
                      <li>{{ $t('Der eingeladene Benutzer erhält vollen Zugriff auf das Kundenkonto') }}</li>
                      <li>{{ $t('Er kann Bestellungen aufgeben und einsehen') }}</li>
                      <li>{{ $t('Er kann keine anderen Benutzer einladen oder löschen') }}</li>
                      <li>{{ $t('Nur der Hauptbenutzer kann Benutzer verwalten') }}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <Link
              :href="route('customerAccount')"
              class="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
            >
              {{ $t('Abbrechen') }}
            </Link>
            <button
              type="submit"
              :disabled="isProcessing"
              class="px-4 py-2 bg-sonnenglas-600 text-white text-sm font-medium rounded-md hover:bg-sonnenglas-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isProcessing" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ $t('Einladung wird gesendet...') }}
              </span>
              <span v-else>{{ $t('Einladung senden') }}</span>
            </button>
          </div>
        </form>

        <!-- Existing Users List -->
        <div v-if="users.length > 0" class="mt-8 bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('Aktuelle Benutzer') }}</h2>
          </div>
          <ul class="divide-y divide-gray-200">
            <li v-for="user in users" :key="user.id" class="px-6 py-4">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ user.name }}</p>
                  <p class="text-sm text-gray-500">{{ user.email }}</p>
                  <span v-if="user.is_primary" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sonnenglas-100 text-sonnenglas-800 mt-1">
                    {{ $t('Hauptbenutzer') }}
                  </span>
                </div>
                <div v-if="!user.is_primary && currentUser.is_primary" class="ml-4">
                  <button
                    @click="removeUser(user.id)"
                    class="text-red-600 hover:text-red-700 text-sm font-medium"
                  >
                    {{ $t('Entfernen') }}
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, reactive, inject } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import AppLayout from '@/layouts/AppLayout.vue'

const route = inject<(name?: string, params?: any, absolute?: boolean) => string>('route')

interface User {
  id: number
  name: string
  email: string
  is_primary: boolean
}

interface Props {
  users: User[]
  currentUser: User
}

const props = defineProps<Props>()

const form = reactive({
  name: '',
  email: ''
})

const errors = ref<Record<string, string>>({})
const isProcessing = ref(false)
const successMessage = ref('')

const sendInvitation = () => {
  if (!route) {
    console.error('Route helper not available')
    return
  }

  errors.value = {}
  successMessage.value = ''

  // Validate
  if (!form.name.trim()) {
    errors.value.name = trans('Name ist erforderlich')
  }

  if (!form.email.trim()) {
    errors.value.email = trans('E-Mail-Adresse ist erforderlich')
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.value.email = trans('Ungültige E-Mail-Adresse')
  }

  // Check if email already exists
  if (props.users.some(u => u.email === form.email)) {
    errors.value.email = trans('Ein Benutzer mit dieser E-Mail-Adresse existiert bereits')
  }

  if (Object.keys(errors.value).length > 0) {
    return
  }

  isProcessing.value = true

  router.post(route('sendUserInvitation'), form, {
    preserveState: true,
    preserveScroll: true,
    onSuccess: () => {
      successMessage.value = trans('Einladung wurde erfolgreich an :email gesendet.', { email: form.email })
      form.name = ''
      form.email = ''
      setTimeout(() => {
        successMessage.value = ''
      }, 5000)
    },
    onError: (errorBag) => {
      errors.value = errorBag
    },
    onFinish: () => {
      isProcessing.value = false
    }
  })
}

const removeUser = (userId: number) => {
  if (!route) {
    console.error('Route helper not available')
    return
  }

  if (confirm(trans('Sind Sie sicher, dass Sie diesen Benutzer entfernen möchten?'))) {
    router.delete(route('deleteUser', userId), {
      preserveState: false,
      preserveScroll: false
    })
  }
}
</script>